'use client';

import { Button, PageBody, PageShell } from '@routly/ui';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { LoginScreen } from '@/components/auth/LoginScreen';
import { useAuth } from '@/hooks/useAuth';
import { ApiError, apiFetch, setTokenGetter } from '@/lib/api';
import type { UserRole } from '@/lib/types';
import { Sidebar } from './Sidebar';

interface AppLayoutProps {
  children: React.ReactNode;
}

function useBypassAuth() {
  return (
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    window.location.search.includes('bypass=true')
  );
}

/**
 * The console's chrome: the rail, the bar above the page, and the page frame
 * every screen sits in.
 *
 * `PageShell`/`PageBody` are the product's, from `@routly/ui`, so padding and
 * the space between sections are decided in one place for both applications —
 * the console used to put `p-6` here and `space-y-6` inside each page, half
 * again what the same screen takes in the product. They live in the layout
 * rather than in each page because a page's early return — the spinner, the
 * failure card — has to sit in the frame too.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <PageShell>
            <PageBody>{children}</PageBody>
          </PageShell>
        </div>
      </div>
    </div>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  const { isAuthenticated, isLoading, getAccessToken, logout } = useAuth();
  const bypassAuth = useBypassAuth();
  const [tokenReady, setTokenReady] = useState(false);

  // Set up token getter for API calls
  useEffect(() => {
    if (bypassAuth) return;
    setTokenGetter(async () => {
      try {
        return await getAccessToken();
      } catch {
        return '';
      }
    });
    setTokenReady(true);
  }, [getAccessToken, bypassAuth]);

  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<{ role: UserRole }>('/v1/users/me'),
    enabled: isAuthenticated && tokenReady && !bypassAuth,
    retry: false,
  });

  // Bypass auth check in development
  if (bypassAuth) {
    return <Shell>{children}</Shell>;
  }

  if (isLoading || (isAuthenticated && !bypassAuth && (!tokenReady || isProfileLoading))) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  if (!bypassAuth && profileError) {
    const message =
      profileError instanceof ApiError ? profileError.message : 'Failed to verify admin access';
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="max-w-md text-center space-y-3">
          <ShieldAlert className="h-10 w-10 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Access check failed</h2>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
    );
  }

  if (!bypassAuth && profile?.role !== 'SUPER_ADMIN') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="max-w-md text-center space-y-4">
          <ShieldAlert className="h-10 w-10 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Access denied</h2>
          <p className="text-sm text-muted-foreground">
            You do not have permission to access the admin panel.
          </p>
          <Button variant="outline" onClick={() => logout()}>
            Log out
          </Button>
        </div>
      </div>
    );
  }

  return <Shell>{children}</Shell>;
}
